import React, { useState, useEffect } from "react"
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Skeleton,
    TextField,
} from "@mui/material"
import LinkIcon from "@mui/icons-material/Link"
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as documentsService from "../services/documentsService"
import DocumentActions from "../components/DocumentActions"
import DocumentPreviewDialog from "../components/exports/DocumentPreviewDialog"
import { useCompanyId } from "../hooks/useCompanyId"

const TYPE_TRANSLATION_KEYS: Record<string, string> = {
    invoice: "documentDetail.types.invoice",
    credit_note: "documentDetail.types.creditNote",
    receipt: "documentDetail.types.receipt",
    quotation: "documentDetail.types.quotation",
    sales_order: "documentDetail.types.salesOrder",
    proforma: "documentDetail.types.proforma",
}

const TYPE_CHIP_COLORS: Record<
    string,
    "primary" | "warning" | "success" | "secondary" | "info" | "default"
> = {
    invoice: "primary",
    credit_note: "warning",
    receipt: "success",
    quotation: "secondary",
    sales_order: "info",
    proforma: "default",
}

const STATUS_COLORS: Record<string, "default" | "success" | "error" | "info"> =
    {
        validated: "success",
        refunded: "info",
    }

const parseVatRate = (rawRate: any): number | null => {
    if (rawRate === null || rawRate === undefined || rawRate === "") return null

    if (typeof rawRate === "number" && Number.isFinite(rawRate)) {
        return rawRate
    }

    if (typeof rawRate === "string") {
        const cleaned = rawRate.trim().replace("%", "").replace(",", ".")
        const parsed = Number(cleaned)
        return Number.isFinite(parsed) ? parsed : null
    }

    return null
}

const computeVatBreakdownFromLines = (
    sourceLines: any[]
): Array<{ vat_rate_id: string; net_amount: number; vat_amount: number }> => {
    const grouped = new Map<
        string,
        { net_amount: number; vat_amount: number }
    >()

    sourceLines.forEach((line: any) => {
        const rate = parseVatRate(
            line?.vat_rate ?? line?.vat_rate_percent ?? line?.vat_rate_id
        )
        const rateKey = rate === null ? "unknown" : String(rate)

        const explicitNet = Number(line?.net_minor ?? line?.line_net)
        const explicitVat = Number(line?.vat_minor ?? line?.line_vat)
        const explicitGross = Number(line?.gross_minor ?? line?.line_gross)

        const quantity = Number(line?.quantity ?? 1)
        const unitPriceMinor = Number(
            line?.unit_price_minor ?? line?.unit_price ?? 0
        )

        const grossMinor = Number.isFinite(explicitGross)
            ? explicitGross
            : quantity * unitPriceMinor

        let netMinor = Number.isFinite(explicitNet) ? explicitNet : 0
        let vatMinor = Number.isFinite(explicitVat) ? explicitVat : 0

        if (!Number.isFinite(explicitNet) || !Number.isFinite(explicitVat)) {
            if (rate !== null) {
                netMinor = Math.round(grossMinor / (1 + rate / 100))
                vatMinor = grossMinor - netMinor
            } else {
                netMinor = grossMinor
                vatMinor = 0
            }
        }

        const current = grouped.get(rateKey) || { net_amount: 0, vat_amount: 0 }
        current.net_amount += netMinor
        current.vat_amount += vatMinor
        grouped.set(rateKey, current)
    })

    return Array.from(grouped.entries()).map(([vat_rate_id, amounts]) => ({
        vat_rate_id,
        net_amount: amounts.net_amount,
        vat_amount: amounts.vat_amount,
    }))
}

const formatVatRateLabel = (vatRateId: string, t: any): string => {
    if (!vatRateId || vatRateId === "unknown") {
        return String(
            t("documentDetail.vat.unknownRate", {
                defaultValue: "TVA non définie",
            })
        )
    }

    return /^\d+(?:[.,]\d+)?$/.test(vatRateId) ? `${vatRateId}%` : vatRateId
}

const DocumentDetailPage: React.FC = () => {
    const { id: documentId } = useParams<{ id: string }>()
    const { companyId } = useCompanyId()
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    const locale = i18n.language || "fr"

    const [document, setDocument] = useState<any>(null)
    const [lines, setLines] = useState<any[]>([])
    const [globalDiscount, setGlobalDiscount] = useState<{
        type: string | null
        value: number | null
        amount_minor: number | null
    } | null>(null)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false)
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
    const [creditAmountInput, setCreditAmountInput] = useState("")
    const [creditNotesTotalMinor, setCreditNotesTotalMinor] = useState(0)
    const [creditNotesLoading, setCreditNotesLoading] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewHtml, setPreviewHtml] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    const loadDocument = async () => {
        if (!companyId || !documentId) return

        try {
            setLoading(true)
            const {
                document: doc,
                lines: docLines,
                globalDiscount: gd,
            } = await documentsService.getDocument(companyId, documentId)
            setDocument(doc)
            setLines(docLines || [])
            setGlobalDiscount(gd)
        } catch (error) {
            console.error("Failed to load document:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadDocument()
    }, [companyId, documentId])

    useEffect(() => {
        const loadCreditNotesTotals = async () => {
            if (
                !companyId ||
                !document ||
                document.document_type !== "invoice"
            ) {
                setCreditNotesTotalMinor(0)
                return
            }

            const creditNoteIds: string[] =
                document.links?.related_credit_note_ids || []
            if (creditNoteIds.length === 0) {
                setCreditNotesTotalMinor(0)
                return
            }

            setCreditNotesLoading(true)
            try {
                const docs = await Promise.all(
                    creditNoteIds.map(async (creditNoteId) => {
                        try {
                            const { document: creditNote } =
                                await documentsService.getDocument(
                                    companyId,
                                    creditNoteId
                                )
                            return creditNote
                        } catch {
                            return null
                        }
                    })
                )

                const totalMinor = docs.reduce((sum, docItem: any) => {
                    return sum + Number(docItem?.totals?.gross_total || 0)
                }, 0)

                setCreditNotesTotalMinor(totalMinor)
            } finally {
                setCreditNotesLoading(false)
            }
        }

        loadCreditNotesTotals()
    }, [
        companyId,
        document?.id,
        document?.document_type,
        document?.links?.related_credit_note_ids?.join("|"),
    ])

    const invoiceGrossTotalMinor = Number(document?.totals?.gross_total || 0)
    const invoiceNetTotalMinor = Number(document?.totals?.net_total || 0)
    const remainingCreditMinor = Math.max(
        0,
        invoiceGrossTotalMinor - creditNotesTotalMinor
    )

    const formatMinor = (minor: number) =>
        `${(minor / 100).toFixed(2)} ${document?.currency || "EUR"}`

    const parseAmountToMinor = (value: string) => {
        const normalized = String(value || "")
            .trim()
            .replace(",", ".")
        const parsed = Number(normalized)
        if (!Number.isFinite(parsed)) return NaN
        return Math.round(parsed * 100)
    }

    const openCreditNoteDialog = () => {
        if (document?.document_type !== "invoice") return

        if (remainingCreditMinor <= 0) {
            setSnackbar({
                open: true,
                message: t("documentDetail.notifications.noRemainingCredit"),
                severity: "error",
            })
            return
        }

        setCreditAmountInput((remainingCreditMinor / 100).toFixed(2))
        setCreditNoteDialogOpen(true)
    }

    const handleCreateCreditNote = async () => {
        if (!companyId || !documentId) return

        const amountMinor = parseAmountToMinor(creditAmountInput)
        if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
            setSnackbar({
                open: true,
                message: t("documentDetail.notifications.invalidAmount"),
                severity: "error",
            })
            return
        }

        if (amountMinor > remainingCreditMinor) {
            setSnackbar({
                open: true,
                message: t(
                    "documentDetail.notifications.exceedsRemainingCredit",
                    {
                        amount: formatMinor(remainingCreditMinor),
                    }
                ),
                severity: "error",
            })
            return
        }

        const ratio =
            invoiceGrossTotalMinor > 0
                ? amountMinor / invoiceGrossTotalMinor
                : 0
        let proratedNet = Math.round(invoiceNetTotalMinor * ratio)
        let proratedVat = amountMinor - proratedNet
        if (proratedVat < 0) {
            proratedVat = 0
            proratedNet = amountMinor
        }

        const proratedVatBreakdown = (vatBreakdown || []).map((row) => ({
            vat_rate_id: row.vat_rate_id,
            net_amount: Math.round(Number(row.net_amount || 0) * ratio),
            vat_amount: Math.round(Number(row.vat_amount || 0) * ratio),
        }))

        if (proratedVatBreakdown.length > 0) {
            const netDelta =
                proratedNet -
                proratedVatBreakdown.reduce(
                    (sum, row) => sum + Number(row.net_amount || 0),
                    0
                )
            const vatDelta =
                proratedVat -
                proratedVatBreakdown.reduce(
                    (sum, row) => sum + Number(row.vat_amount || 0),
                    0
                )
            proratedVatBreakdown[0].net_amount += netDelta
            proratedVatBreakdown[0].vat_amount += vatDelta
        }
        setActionLoading(true)

        try {
            const creditNote = await documentsService.createCreditNote(
                companyId,
                documentId,
                {
                    outlet_id: document?.outlet_id,
                    customer_id: document?.customer_id,
                    totals: {
                        net_total: proratedNet,
                        vat_total: proratedVat,
                        gross_total: amountMinor,
                    },
                    vat_breakdown: proratedVatBreakdown,
                }
            )
            await loadDocument()
            setCreditNoteDialogOpen(false)
            setCreditAmountInput("")
            setSnackbar({
                open: true,
                message: t("documentDetail.notifications.creditNoteCreated"),
                severity: "success",
            })
            if (creditNote?.id) {
                navigate(`/documents/${creditNote.id}`)
            }
        } catch (error: any) {
            console.error("Failed to create credit note:", error)
            setSnackbar({
                open: true,
                message:
                    error?.message ||
                    t("documentDetail.notifications.createCreditNoteError"),
                severity: "error",
            })
        } finally {
            setActionLoading(false)
        }
    }

    const handleCreateInvoice = async () => {
        if (!companyId || !documentId) return
        setActionLoading(true)

        try {
            const invoice = await documentsService.createInvoiceFromReceipt(
                companyId,
                documentId
            )
            setInvoiceDialogOpen(false)
            setSnackbar({
                open: true,
                message: t("documentDetail.notifications.invoiceCreated"),
                severity: "success",
            })
            navigate(`/documents/${invoice.id}`)
        } catch (error: any) {
            console.error("Failed to create invoice:", error)
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    t("documentDetail.notifications.createInvoiceError"),
                severity: "error",
            })
        } finally {
            setActionLoading(false)
        }
    }

    const handleDownloadPdf = async () => {
        if (!companyId || !documentId) return
        try {
            await documentsService.downloadPdf(companyId, documentId)
        } catch (error: any) {
            console.error("Failed to download PDF:", error)
            setSnackbar({
                open: true,
                message:
                    error.message ||
                    t("documentDetail.notifications.downloadError"),
                severity: "error",
            })
        }
    }

    const handlePrint = async () => {
        if (!companyId || !documentId) return
        try {
            const html = await documentsService.getHtmlPreview(
                companyId,
                documentId
            )
            const printWindow = window.open("", "_blank")
            if (printWindow) {
                printWindow.document.write(html)
                printWindow.document.close()
                printWindow.onload = () => {
                    printWindow.onafterprint = () => {
                        printWindow.close()
                    }
                    printWindow.print()
                    window.setTimeout(() => {
                        if (!printWindow.closed) {
                            printWindow.close()
                        }
                    }, 1500)
                }
            }
        } catch (error) {
            console.error("Failed to print document:", error)
            setSnackbar({
                open: true,
                message: t("documentDetail.notifications.printError"),
                severity: "error",
            })
        }
    }

    const handlePreview = async () => {
        if (!companyId || !documentId) return
        setPreviewOpen(true)
        setPreviewLoading(true)
        setPreviewHtml(null)
        try {
            const html = await documentsService.getHtmlPreview(
                companyId,
                documentId
            )
            setPreviewHtml(html)
        } catch (error) {
            console.error("Failed to load preview:", error)
            setSnackbar({
                open: true,
                message: t("documentDetail.notifications.loadError"),
                severity: "error",
            })
        } finally {
            setPreviewLoading(false)
        }
    }

    const handleBack = () => {
        navigate("/documents")
    }

    if (loading || !document) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ my: 4 }}>
                    <Skeleton
                        variant="text"
                        width={140}
                        height={20}
                        sx={{ mb: 2 }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                width={80}
                                height={32}
                            />
                            <Skeleton variant="text" width={200} height={40} />
                            <Skeleton
                                variant="rounded"
                                width={70}
                                height={24}
                            />
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Skeleton
                                variant="rounded"
                                width={80}
                                height={36}
                            />
                            <Skeleton
                                variant="rounded"
                                width={90}
                                height={36}
                            />
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rounded" height={200} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rounded" height={200} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rounded" height={140} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rounded" height={140} />
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        )
    }

    const links = document.links || {}
    const documentCustomer = document.customer_snapshot || {}
    const customerDisplayName =
        documentCustomer?.name || documentCustomer?.company_name || "—"
    const embeddedItems =
        (links.embeded || links.embedded) && Array.isArray(links.items)
            ? links.items
            : []
    const isEmbeddedInvoiceWithItems =
        document.document_type === "invoice" && embeddedItems.length > 0
    const displayedLines =
        lines.length > 0
            ? lines
            : isEmbeddedInvoiceWithItems
            ? embeddedItems.flatMap((item: any) => {
                  const quantity = Number(item?.qty ?? 1)
                  const unitPriceMinor = Number(item?.unitPrice ?? 0)
                  const mapped: any[] = [
                      {
                          label: item?.catName || item?.name || "—",
                          quantity,
                          unit_price_minor: unitPriceMinor,
                          gross_minor: quantity * unitPriceMinor,
                          vat_rate_id: null,
                      },
                  ]
                  const warrantyPriceMinor = Number(item?.warantyPrice ?? 0)
                  if (warrantyPriceMinor > 0) {
                      mapped.push({
                          label: `Garantie ${
                              item?.catName || item?.name || ""
                          }`.trim(),
                          quantity,
                          unit_price_minor: warrantyPriceMinor,
                          gross_minor: quantity * warrantyPriceMinor,
                          vat_rate_id: null,
                      })
                  }
                  return mapped
              })
            : []

    // Normalize vat_breakdown: DB may return an object keyed by rate instead of an array
    const documentVatBreakdown: Array<{
        vat_rate_id: string
        net_amount: number
        vat_amount: number
    }> = (() => {
        const raw = document.vat_breakdown
        if (!raw) return []
        if (Array.isArray(raw)) {
            return raw.map((row: any) => ({
                // Accept `vat_rate` as a fallback for documents synced from
                // external apps before the field was normalized to
                // `vat_rate_id`.
                vat_rate_id: String(
                    row?.vat_rate_id ?? row?.vat_rate ?? "unknown"
                ),
                net_amount: Number(
                    row?.net_total_minor ?? row?.net_amount ?? 0
                ),
                vat_amount: Number(
                    row?.vat_total_minor ?? row?.vat_amount ?? 0
                ),
            }))
        }
        return Object.entries(raw).map(([rate, v]: [string, any]) => ({
            vat_rate_id: String(rate || "unknown"),
            net_amount: Number(v.net_total_minor ?? v.net_amount ?? 0),
            vat_amount: Number(v.vat_total_minor ?? v.vat_amount ?? 0),
        }))
    })()

    // Use persisted vat_breakdown from document if available and non-empty,
    // otherwise fallback to computed breakdown from lines
    const hasDocumentVatBreakdown =
        documentVatBreakdown.length > 0 &&
        documentVatBreakdown.some(
            (row) =>
                Number(row.net_amount || 0) !== 0 ||
                Number(row.vat_amount || 0) !== 0
        )
    const vatBreakdown = hasDocumentVatBreakdown
        ? documentVatBreakdown
        : computeVatBreakdownFromLines(displayedLines)
    const hasMultipleVatRates = vatBreakdown.length > 1

    const hasLinks =
        links.source_quotation_id ||
        links.source_order_id ||
        links.source_invoice_id ||
        links.source_receipt_id ||
        links.related_invoice_id ||
        (links.related_credit_note_ids &&
            links.related_credit_note_ids.length > 0) ||
        links.sale_id

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
                <Box sx={{ mb: 2 }}>
                    <Button variant="text" onClick={handleBack}>
                        ← {t("documentDetail.back")}
                    </Button>
                </Box>

                {/* Header with type badge, number, status */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 1,
                            }}
                        >
                            <Chip
                                label={
                                    TYPE_TRANSLATION_KEYS[
                                        document.document_type
                                    ]
                                        ? t(
                                              TYPE_TRANSLATION_KEYS[
                                                  document.document_type
                                              ]
                                          )
                                        : document.document_type
                                }
                                color={
                                    TYPE_CHIP_COLORS[document.document_type] ||
                                    "default"
                                }
                                variant="outlined"
                            />
                            <Typography variant="h4">
                                {document.number ||
                                    t("documentDetail.unnumbered")}
                            </Typography>
                            <Chip
                                label={document.status}
                                color={
                                    STATUS_COLORS[document.status] || "default"
                                }
                                size="small"
                            />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {t("documentDetail.metadata.created", {
                                date: new Date(
                                    document.created_at
                                ).toLocaleString(locale),
                            })}
                            {document.validated_at
                                ? ` · ${t("documentDetail.metadata.validated", {
                                      date: new Date(
                                          document.validated_at
                                      ).toLocaleString(locale),
                                  })}`
                                : ""}
                        </Typography>
                    </Box>

                    {/* T019: DocumentActions component */}
                    <DocumentActions
                        document={document}
                        onCreateCreditNote={openCreditNoteDialog}
                        onCreateInvoice={() => setInvoiceDialogOpen(true)}
                        onDownloadPdf={handleDownloadPdf}
                        onPrint={handlePrint}
                        onPreview={handlePreview}
                        loading={actionLoading}
                        canCreateCreditNote={
                            creditNotesLoading || remainingCreditMinor > 0
                        }
                    />
                </Box>

                <Grid container spacing={3}>
                    {/* Document Information */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t("documentDetail.documentInfo.title")}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "documentDetail.documentInfo.type"
                                            )}
                                        </Typography>
                                        <Typography variant="body1">
                                            {TYPE_TRANSLATION_KEYS[
                                                document.document_type
                                            ]
                                                ? t(
                                                      TYPE_TRANSLATION_KEYS[
                                                          document.document_type
                                                      ]
                                                  )
                                                : document.document_type}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "documentDetail.documentInfo.status"
                                            )}
                                        </Typography>
                                        <Chip
                                            label={document.status}
                                            color={
                                                STATUS_COLORS[
                                                    document.status
                                                ] || "default"
                                            }
                                            size="small"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "documentDetail.documentInfo.created"
                                            )}
                                        </Typography>
                                        <Typography variant="body1">
                                            {new Date(
                                                document.created_at
                                            ).toLocaleString(locale)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "documentDetail.documentInfo.client"
                                            )}
                                        </Typography>
                                        <Typography variant="body1">
                                            {customerDisplayName}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "documentDetail.documentInfo.validated"
                                            )}
                                        </Typography>
                                        <Typography variant="body1">
                                            {document.validated_at
                                                ? new Date(
                                                      document.validated_at
                                                  ).toLocaleString(locale)
                                                : "—"}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-end",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            mb: 1,
                                            display: "flex",
                                            gap: 2,
                                            width: "100%",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {t("documentDetail.totals.net")}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight={500}
                                        >
                                            {(
                                                (document.totals?.net_total ||
                                                    0) / 100
                                            ).toFixed(2)}{" "}
                                            {document.currency}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            mb: 1,
                                            display: "flex",
                                            gap: 2,
                                            width: "100%",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {t("documentDetail.totals.vat")}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight={500}
                                        >
                                            {(
                                                (document.totals?.vat_total ||
                                                    0) / 100
                                            ).toFixed(2)}{" "}
                                            {document.currency}
                                        </Typography>
                                    </Box>
                                    {globalDiscount &&
                                        globalDiscount.amount_minor &&
                                        globalDiscount.amount_minor > 0 && (
                                            <Box
                                                sx={{
                                                    mb: 1,
                                                    display: "flex",
                                                    gap: 2,
                                                    width: "100%",
                                                    justifyContent:
                                                        "space-between",
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    sx={{ color: "#ef4444" }}
                                                >
                                                    {t(
                                                        "documentDetail.totals.globalDiscount"
                                                    )}
                                                    {globalDiscount.type ===
                                                        "percentage" &&
                                                    globalDiscount.value
                                                        ? ` (${globalDiscount.value}%)`
                                                        : ""}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight={500}
                                                    sx={{ color: "#ef4444" }}
                                                >
                                                    −
                                                    {(
                                                        globalDiscount.amount_minor /
                                                        100
                                                    ).toFixed(2)}{" "}
                                                    {document.currency}
                                                </Typography>
                                            </Box>
                                        )}
                                    <Divider sx={{ width: "100%", my: 1 }} />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            width: "100%",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Typography variant="h6">
                                            {t("documentDetail.totals.gross")}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                        >
                                            {(
                                                (document.totals?.gross_total ||
                                                    0) / 100
                                            ).toFixed(2)}{" "}
                                            {document.currency}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {t("documentDetail.vat.title")}
                            </Typography>
                            {hasMultipleVatRates && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 1 }}
                                >
                                    {t("documentDetail.vat.mixedRates", {
                                        defaultValue:
                                            "TVA mixte : plusieurs taux détectés",
                                    })}
                                </Typography>
                            )}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {t("documentDetail.vat.rate")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t("documentDetail.vat.net")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t("documentDetail.vat.vat")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t("documentDetail.vat.gross")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {vatBreakdown.map(
                                            (vat: any, index: number) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {formatVatRateLabel(
                                                            String(
                                                                vat.vat_rate_id ||
                                                                    "unknown"
                                                            ),
                                                            t
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {(
                                                            (vat.net_amount ||
                                                                0) / 100
                                                        ).toFixed(2)}{" "}
                                                        {document.currency}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {(
                                                            (vat.vat_amount ||
                                                                0) / 100
                                                        ).toFixed(2)}{" "}
                                                        {document.currency}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {(
                                                            ((vat.net_amount ||
                                                                0) +
                                                                (vat.vat_amount ||
                                                                    0)) /
                                                            100
                                                        ).toFixed(2)}{" "}
                                                        {document.currency}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                        {vatBreakdown.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    align="center"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {t(
                                                            "documentDetail.vat.empty"
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* T017: Linked Documents */}
                    {hasLinks && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <LinkIcon color="action" />
                                    <Typography variant="h6">
                                        {t("documentDetail.links.title")}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {links.source_quotation_id && (
                                        <Chip
                                            label={t(
                                                "documentDetail.links.sourceQuotation"
                                            )}
                                            component={RouterLink}
                                            to={`/documents/${links.source_quotation_id}`}
                                            clickable
                                            variant="outlined"
                                            color="secondary"
                                        />
                                    )}
                                    {links.source_order_id && (
                                        <Chip
                                            label={t(
                                                "documentDetail.links.sourceOrder"
                                            )}
                                            component={RouterLink}
                                            to={`/documents/${links.source_order_id}`}
                                            clickable
                                            variant="outlined"
                                            color="info"
                                        />
                                    )}
                                    {links.source_invoice_id && (
                                        <Chip
                                            label={t(
                                                "documentDetail.links.sourceInvoice"
                                            )}
                                            component={RouterLink}
                                            to={`/documents/${links.source_invoice_id}`}
                                            clickable
                                            variant="outlined"
                                            color="primary"
                                        />
                                    )}
                                    {links.source_receipt_id && (
                                        <Chip
                                            label={t(
                                                "documentDetail.links.sourceReceipt"
                                            )}
                                            component={RouterLink}
                                            to={`/documents/${links.source_receipt_id}`}
                                            clickable
                                            variant="outlined"
                                            color="success"
                                        />
                                    )}
                                    {links.related_invoice_id && (
                                        <Chip
                                            label={t(
                                                "documentDetail.links.invoice"
                                            )}
                                            component={RouterLink}
                                            to={`/documents/${links.related_invoice_id}`}
                                            clickable
                                            variant="outlined"
                                            color="primary"
                                        />
                                    )}
                                    {links.related_credit_note_ids?.map(
                                        (cnId: string) => (
                                            <Chip
                                                key={cnId}
                                                label={t(
                                                    "documentDetail.links.creditNote"
                                                )}
                                                component={RouterLink}
                                                to={`/documents/${cnId}`}
                                                clickable
                                                variant="outlined"
                                                color="warning"
                                            />
                                        )
                                    )}
                                    {links.sale_id && (
                                        <Chip
                                            label={t(
                                                "documentDetail.links.sale",
                                                {
                                                    id: `${links.sale_id.substring(
                                                        0,
                                                        8
                                                    )}...`,
                                                }
                                            )}
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {/* Credit Note Confirmation Dialog */}
                <Dialog
                    open={creditNoteDialogOpen}
                    onClose={() => setCreditNoteDialogOpen(false)}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>
                        {t("documentDetail.creditNoteDialog.title", {
                            number: document?.number,
                        })}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {t("documentDetail.creditNoteDialog.remaining", {
                                amount: formatMinor(remainingCreditMinor),
                            })}
                        </Typography>
                        <TextField
                            label={t(
                                "documentDetail.creditNoteDialog.amountLabel"
                            )}
                            type="number"
                            value={creditAmountInput}
                            onChange={(event) =>
                                setCreditAmountInput(event.target.value)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreditNoteDialogOpen(false)}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleCreateCreditNote}
                            disabled={actionLoading}
                        >
                            {t("documentDetail.creditNoteDialog.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Invoice creation confirmation */}
                <Dialog
                    open={invoiceDialogOpen}
                    onClose={() => setInvoiceDialogOpen(false)}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>
                        {t("documentDetail.invoiceDialog.title")}
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            {t("documentDetail.invoiceDialog.description")}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setInvoiceDialogOpen(false)}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateInvoice}
                            disabled={actionLoading}
                        >
                            {t("documentDetail.invoiceDialog.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Line Items / Embedded Articles */}
                {displayedLines.length > 0 && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {isEmbeddedInvoiceWithItems
                                    ? t("documentDetail.lines.title")
                                    : t("documentDetail.lines.lineItems")}
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {t("documentDetail.lines.item")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t("documentDetail.lines.qty")}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t(
                                                    "documentDetail.lines.unitPrice"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t(
                                                    "documentDetail.lines.discount"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t(
                                                    "documentDetail.lines.vatRate"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t(
                                                    "documentDetail.lines.totalTtc"
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {displayedLines.map(
                                            (line: any, index: number) => {
                                                const discountMinor = Number(
                                                    line.discount_amount_minor ||
                                                        0
                                                )
                                                return (
                                                    <TableRow
                                                        key={line.id || index}
                                                    >
                                                        <TableCell>
                                                            {line.label ||
                                                                line.item_name ||
                                                                "—"}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {line.quantity}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {(
                                                                (line.unit_price_minor ??
                                                                    line.unit_price ??
                                                                    0) / 100
                                                            ).toFixed(2)}{" "}
                                                            {document.currency}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {discountMinor >
                                                            0 ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: "#ef4444",
                                                                    }}
                                                                >
                                                                    −
                                                                    {(
                                                                        discountMinor /
                                                                        100
                                                                    ).toFixed(
                                                                        2
                                                                    )}{" "}
                                                                    {
                                                                        document.currency
                                                                    }
                                                                    {line.discount_type ===
                                                                        "percentage" &&
                                                                    line.discount_value
                                                                        ? ` (${line.discount_value}%)`
                                                                        : ""}
                                                                </Typography>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {line.vat_rate_id
                                                                ? `${line.vat_rate_id}`
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {(
                                                                (line.gross_minor ??
                                                                    line.line_gross ??
                                                                    0) / 100
                                                            ).toFixed(2)}{" "}
                                                            {document.currency}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            }
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                )}
                {/* Preview dialog */}
                <DocumentPreviewDialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    html={previewHtml}
                    loading={previewLoading}
                    documentNumber={document?.number || "UNNUMBERED"}
                    documentType={document?.document_type}
                />

                {/* Snackbar for action feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    )
}

export default DocumentDetailPage
